"use client";

import { useLayoutEffect, useRef, useState } from "react";

import cn from "classnames";

import { brown } from "@/app/font";

export function CustomerReviewQuote({ quote }: CustomerReviewQuoteProps) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const trimmedQuote = quote?.trim();

  useLayoutEffect(() => {
    const node = quoteRef.current;
    if (!node) return;

    const updateOverflow = () => {
      if (expanded) return;
      setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
    };

    updateOverflow();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(node);
    return () => observer.disconnect();
  }, [trimmedQuote, expanded]);

  if (!trimmedQuote) return null;

  const showMore = isOverflowing && !expanded;

  return (
    <div className="flex flex-col items-start gap-2">
      <p
        ref={quoteRef}
        data-testid="customer-review-quote"
        className={cn(
          brown.className,
          "pre-header-lg text-greyscale-body m-0",
          !expanded && "line-clamp-6",
        )}>
        &ldquo;{trimmedQuote}&rdquo;
      </p>
      {showMore ? (
        <button
          type="button"
          className="text-greyscale-body body-4 focus-visible:ring-secondary cursor-pointer rounded-sm underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          data-testid="customer-review-see-more"
          onClick={() => setExpanded(true)}>
          See more
        </button>
      ) : null}
    </div>
  );
}

type CustomerReviewQuoteProps = {
  quote: string;
};
